<script setup lang="ts">
// /about — mockup page 8 (M10), rebuilt around the mission after the
// open-items review (items 4, 5, 6 and 7).
//
// Four things from the mockup are gone, each by decision rather than for
// want of a value to fill in:
//
//   · the stats bar (100,000+ homeowners, 50,000+ assessments) — no data
//     behind either number;
//   · the review score — the site has no review history of its own;
//   · the team row and the named-founder card — stock-photo strangers
//     captioned as staff, and a person the page doesn't need. The site is
//     about the mission, not an individual;
//   · "How this site is paid for", including any claim that Ask-HVAC is
//     "not paid by any HVAC company". The revenue model isn't the About
//     page's business, and a disclosure written today would age into an
//     inaccuracy as the site changes. If an arrangement ever needs
//     disclosing, it gets its own page saying exactly what it is.
//
// What carries the page instead is why the site exists: industry
// experience turned into plain explanations, plus the list of things it
// won't do. Both are checkable against the site itself.
import { RouterLink } from 'vue-router'

import { SITE_REGION } from '../nav'
// Pixabay Content License — see src/assets/ATTRIBUTIONS.md. Borrowed from
// the shouldi brand until the redesign's own photography is sourced.
import heroPhoto from '@/assets/shouldi-dusk.jpg'

const VALUES = [
    {
        title: 'Independent guidance',
        body: 'No manufacturer badge appears anywhere on this site, and we sell no equipment.',
        icon: '<path d="M10 1.6 3 4.6v5.2c0 4.3 3 8.3 7 9.2 4-.9 7-4.9 7-9.2V4.6z"/>',
    },
    {
        title: 'Rules you can read',
        body: 'The repair-or-replace verdict lists every factor and what it added. No hidden weighting.',
        icon: '<path d="M4 2.8h12a1 1 0 0 1 1 1v12.4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3.8a1 1 0 0 1 1-1zm1.8 3.4v1.6h8.4V6.2zm0 3.6v1.6h8.4V9.8zm0 3.6v1.6h5.2v-1.6z"/>',
    },
    {
        title: 'Homeowner first',
        body: "We'll tell you when the cheap fix is the right one, and when to buy nothing at all.",
        icon: '<path d="M10 2.6 2.5 9h2v8h4v-5h3v5h4V9h2z"/>',
    },
    {
        title: 'Local focus',
        body: `Built around ${SITE_REGION} — long cooling seasons, salt air and humidity change the answers.`,
        icon: '<path d="M10 1.8a5.2 5.2 0 0 0-5.2 5.2c0 3.9 5.2 11.2 5.2 11.2s5.2-7.3 5.2-11.2A5.2 5.2 0 0 0 10 1.8zm0 7.2a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>',
    },
    {
        title: 'Your privacy',
        body: 'Answers stay in your browser until you choose to send them. We never sell your details.',
        icon: '<path d="M10 1.8 4 4.3v4.4c0 3.7 2.5 7.1 6 8 3.5-.9 6-4.3 6-8V4.3z"/>',
    },
]

const STEPS = [
    {
        title: 'Get answers',
        body: 'Use the guided troubleshooters, or describe the problem in your own words and let the AI read it.',
    },
    {
        title: 'Explore your options',
        body: 'Compare repair against replacement, and the system tiers against each other, on what actually separates them.',
    },
    {
        title: 'Make the decision',
        body: "You'll have the reasoning, not just the conclusion — including the parts that argue against spending.",
    },
    {
        title: 'Get local help',
        body: 'When you want a professional, your answers go with you so nobody starts from scratch.',
    },
]

const MISSION = [
    {
        title: 'Educate',
        body: 'Break the jargon down far enough that you can follow the reasoning yourself.',
        tone: 'orange',
        icon: '<path d="M9.1 14.2 5.4 10.5l1.4-1.4 2.3 2.3 5-5 1.4 1.4z"/>',
    },
    {
        title: 'Empower',
        body: 'Give you the same numbers a technician is working from, so a quote is something you can check.',
        tone: 'blue',
        icon: '<path d="M11 2 4 12h5l-1 6 7-10h-5z"/>',
    },
    {
        title: 'Connect',
        body: 'Hand you to a local professional when — and only when — the job actually needs one.',
        tone: 'green',
        icon: '<path d="M10 17.4 8.6 16.1C4.2 12.2 1.6 9.8 1.6 6.9A3.9 3.9 0 0 1 5.5 3c1.4 0 2.8.7 3.6 1.8h1.8A4.4 4.4 0 0 1 14.5 3a3.9 3.9 0 0 1 3.9 3.9c0 2.9-2.6 5.3-7 9.2z"/>',
    },
]

/** The publishing rules, stated as a promise rather than hidden in a repo file. */
const WONT = [
    {
        title: 'No review scores or testimonials',
        body: 'There is no star rating, no review count and no customer quote anywhere on this site. We have not earned our own, and we will not borrow anyone else\'s.',
    },
    {
        title: 'No invented track record',
        body: 'You will not find a count of homeowners helped or assessments completed anywhere on this site. If we ever publish a figure, it will be one we can actually count.',
    },
    {
        title: 'No prices presented as quotes',
        body: 'The cost ranges here are broad educational estimates, labelled as such. They are not quotes, not standard industry prices, and not a ceiling on what a fair contractor may charge.',
    },
    {
        title: 'No financing offers',
        body: 'We do not lend, arrange credit, or advertise rates and monthly payments. Financing comes from the contractor you choose, and the terms are theirs to state.',
    },
    {
        title: 'No claims about a contractor network',
        body: 'We do not operate a vetted network or run a licensing check. What we do is pass your request to an HVAC professional working with a licensed HVAC contractor — that is the whole of it.',
    },
    {
        title: 'No scare tactics',
        body: 'Nothing on this site is designed to make you feel that delay is dangerous. When something genuinely is urgent — a gas or burning smell — we say so plainly and tell you to stop reading.',
    },
]

/**
 * Why the site exists (open-items review, item 6).
 *
 * This replaces the mockup's team row and the founder card. The claim it
 * rests on is experience, not credentials on display: nothing here names a
 * person, a licence number or a job title, because none of that is what
 * makes the explanations useful.
 */
const WHY = [
    {
        title: 'Repair or replace',
        body: 'The decision that costs the most to get wrong, usually made under pressure on a hot afternoon with one opinion in hand.',
    },
    {
        title: 'Equipment choices',
        body: 'What actually separates the tiers on a quote sheet, and when the cheapest one is genuinely the right answer.',
    },
    {
        title: 'Indoor air quality',
        body: 'Which add-ons solve a problem you actually have, and which are solving a problem the brochure invented.',
    },
    {
        title: 'What things cost',
        body: 'Broad ranges, plainly labelled as estimates, so a quote is something you can put in context instead of just react to.',
    },
    {
        title: 'The questions to ask',
        body: 'The handful of questions that separate a thorough contractor from a fast one — before you sign anything.',
    },
]

const BEHIND = [
    {
        title: 'Published decision trees',
        body: 'Every guided walk is a contract file — the questions, the options and where each one leads. The browser and the server read the same one, so the advice you saw is the advice that gets recorded.',
    },
    {
        title: 'A scoring rule, not a vibe',
        body: 'The repair-or-replace verdict comes from published weights plus two rules of thumb the trade already uses: age × repair cost over $5,000, and a quote over half the cost of replacing. Both are shown when they fire.',
    },
    {
        title: 'An AI pass with limits',
        body: 'The optional AI read is free, capped per device, and hedged on purpose. It has not inspected your equipment and it says so. A licensed technician confirms before you spend money.',
    },
]
</script>

<template>
    <section class="hero" :style="{ backgroundImage: `url(${heroPhoto})` }">
        <div class="wash" aria-hidden="true"></div>
        <div class="hero-inner">
            <div>
                <p class="crumb">Home <span>›</span> About Ask-HVAC.com</p>
                <h1>
                    We're Here to Help<br />
                    Homeowners Make the<br />
                    <span class="hl">Right HVAC Decisions.</span>
                </h1>
                <p class="lede">
                    Ask-HVAC.com is an independent resource built to give homeowners clear answers
                    and reasoning they can check — before spending a dime.
                </p>
            </div>

            <aside class="stance">
                <h2>Independent of the equipment business</h2>
                <p>
                    We are not a manufacturer, a distributor or an installer. We sell no equipment,
                    fit nothing, and warrant nothing — so no brand has a reason to want a
                    particular answer out of this site.
                </p>
                <p class="link">
                    <a href="#why">Why Ask-HVAC.com exists ↓</a>
                </p>
            </aside>
        </div>
    </section>

    <section class="values">
        <div class="values-inner">
            <div v-for="v in VALUES" :key="v.title" class="value">
                <span class="chip">
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="v.icon"></svg>
                </span>
                <strong>{{ v.title }}</strong>
                <p>{{ v.body }}</p>
            </div>
        </div>
    </section>

    <section class="works">
        <div class="works-inner">
            <div>
                <h2>How Ask-HVAC.com works for you</h2>
                <p class="sub">Answers in minutes, and a local pro only when you want one.</p>

                <ol class="steps">
                    <li v-for="(s, i) in STEPS" :key="s.title">
                        <span class="n">{{ i + 1 }}</span>
                        <div>
                            <strong>{{ s.title }}</strong>
                            <p>{{ s.body }}</p>
                        </div>
                    </li>
                </ol>

                <p class="simple">
                    <strong>It's that simple.</strong> We explain. You decide. Local pros do the
                    work.
                </p>
            </div>

            <aside class="mission">
                <h2>Our mission</h2>
                <p class="sub">
                    To leave a homeowner able to make the call themselves, and to argue for it.
                </p>
                <div v-for="m in MISSION" :key="m.title" class="m">
                    <span class="chip" :data-tone="m.tone">
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="m.icon"></svg>
                    </span>
                    <div>
                        <strong>{{ m.title }}</strong>
                        <p>{{ m.body }}</p>
                    </div>
                </div>
            </aside>
        </div>
    </section>

    <section id="why" class="why">
        <div class="why-inner">
            <div class="why-lede">
                <h2>Why Ask-HVAC.com exists</h2>
                <p class="body">
                    This site was built out of real-world HVAC industry experience, for the
                    conversation that usually goes badly: a homeowner with a system that has
                    stopped working, a quote they have no way to judge, and an afternoon to
                    decide. The knowledge that makes that decision easy already exists — it just
                    tends to stay on the technician's side of the van.
                </p>
                <p class="body">
                    So the purpose here is narrow and practical. Explain what is actually going on
                    inside a heating and cooling system, in language that doesn't require a trade
                    background, and leave a homeowner able to ask better questions and recognise a
                    good answer when they hear one.
                </p>
            </div>

            <ul class="why-list">
                <li v-for="w in WHY" :key="w.title">
                    <strong>{{ w.title }}</strong>
                    <p>{{ w.body }}</p>
                </li>
            </ul>
        </div>
    </section>

    <section class="behind">
        <div class="behind-inner">
            <h2>What's behind the answers</h2>
            <div class="behind-grid">
                <div v-for="b in BEHIND" :key="b.title" class="card">
                    <strong>{{ b.title }}</strong>
                    <p>{{ b.body }}</p>
                </div>
            </div>
        </div>
    </section>

    <section class="wont">
        <div class="wont-inner">
            <div>
                <h2>What this site won't do</h2>
                <p class="sub">
                    Every one of these is something the industry does routinely, and something we
                    have had the chance to do and left out.
                </p>
            </div>
            <ul>
                <li v-for="w in WONT" :key="w.title">
                    <strong>{{ w.title }}</strong>
                    <p>{{ w.body }}</p>
                </li>
            </ul>
        </div>
    </section>

    <section class="closing">
        <div class="closing-inner">
            <h2>Ready to get the answers you need?</h2>
            <p>
                Start with the troubleshooter, run the repair-or-replace numbers, or go straight to
                a free second opinion. Nothing needs an account.
            </p>
            <div class="actions">
                <RouterLink class="cta" to="/second-opinion">Get a Free Second Opinion →</RouterLink>
                <RouterLink class="cta cta--ghost" to="/ask">Troubleshoot my AC</RouterLink>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
/* ---- hero ---- */
.hero {
    position: relative;
    background-size: cover;
    background-position: center;
    color: #fff;
}

.wash {
    position: absolute;
    inset: 0;
    background: var(--site-gradient-hero-overlay);
}

.hero-inner {
    position: relative;
    z-index: 1;
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-xl) var(--site-space-lg);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 21rem;
    gap: var(--site-space-xl);
    align-items: center;

    @media (max-width: 960px) {
        grid-template-columns: 1fr;
    }
}

.crumb {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.76rem;
    color: rgba(255, 255, 255, 0.62);

    span {
        margin: 0 0.3rem;
    }
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.7rem, 3.3vw, 2.5rem);
    font-weight: 800;
    line-height: 1.16;
    margin: 0 0 var(--site-space-md);
    text-shadow: 0 2px 18px rgba(3, 15, 40, 0.45);
}

.hl {
    color: var(--site-accent);
}

.lede {
    max-width: 34rem;
    margin: 0;
    color: rgba(255, 255, 255, 0.88);
    line-height: 1.6;
}

.stance {
    background: rgba(7, 28, 69, 0.84);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);

    h2 {
        font-family: var(--site-font-heading);
        font-size: 0.98rem;
        color: #fff;
        margin: 0 0 var(--site-space-sm);
    }

    p {
        margin: 0;
        font-size: 0.82rem;
        line-height: 1.55;
        color: rgba(255, 255, 255, 0.82);
    }

    .link {
        margin-top: var(--site-space-sm);

        a {
            color: #9ec5f8;
            font-weight: 600;
            text-decoration: none;

            &:hover {
                color: #fff;
            }
        }
    }
}

/* ---- shared ---- */
h2 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.3rem, 2.4vw, 1.7rem);
    font-weight: 700;
    color: var(--site-navy);
    margin: 0 0 0.4rem;
}

.sub {
    margin: 0 0 var(--site-space-lg);
    color: var(--site-text-muted);
    font-size: 0.92rem;
    line-height: 1.6;
}

.values-inner,
.works-inner,
.why-inner,
.behind-inner,
.wont-inner,
.closing-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

/* ---- values strip ---- */
.values {
    border-bottom: 1px solid var(--site-border);
}

.values-inner {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: var(--site-space-lg);
}

.value {
    .chip {
        display: grid;
        place-items: center;
        width: 2.4rem;
        height: 2.4rem;
        margin-bottom: 0.5rem;
        border-radius: var(--site-radius-sm);
        background: var(--site-blue-soft);
        color: var(--site-blue);

        svg {
            width: 1.2rem;
            height: 1.2rem;
        }
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.88rem;
        color: var(--site-navy);
        margin-bottom: 0.2rem;
    }

    p {
        margin: 0;
        font-size: 0.8rem;
        color: var(--site-text-muted);
        line-height: 1.5;
    }
}

/* ---- how it works + mission ---- */
.works {
    background: var(--site-bg-alt);
}

.works-inner {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 21rem;
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
}

.steps {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: var(--site-space-md);

    li {
        display: flex;
        gap: 0.8rem;
        align-items: flex-start;
    }

    .n {
        display: grid;
        place-items: center;
        width: 1.9rem;
        height: 1.9rem;
        flex-shrink: 0;
        border-radius: 50%;
        background: var(--site-blue);
        color: #fff;
        font-family: var(--site-font-heading);
        font-size: 0.85rem;
        font-weight: 700;
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.94rem;
        color: var(--site-navy);
    }

    p {
        margin: 0.15rem 0 0;
        font-size: 0.85rem;
        color: var(--site-text-muted);
        line-height: 1.55;
    }
}

.simple {
    margin: var(--site-space-lg) 0 0;
    padding: 0.7rem 0.9rem;
    border-radius: var(--site-radius-sm);
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    font-size: 0.88rem;

    strong {
        color: var(--site-navy);
    }
}

.mission {
    padding: var(--site-space-lg);
    border-radius: var(--site-radius-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);

    h2 {
        font-size: 1.15rem;
    }

    .sub {
        margin-bottom: var(--site-space-md);
        font-size: 0.85rem;
    }
}

.m {
    display: flex;
    gap: 0.7rem;
    margin-bottom: var(--site-space-md);

    &:last-child {
        margin-bottom: 0;
    }

    .chip {
        display: grid;
        place-items: center;
        width: 2.1rem;
        height: 2.1rem;
        flex-shrink: 0;
        border-radius: 50%;
        color: #fff;

        &[data-tone='orange'] {
            background: var(--site-accent);
        }

        &[data-tone='blue'] {
            background: var(--site-blue);
        }

        &[data-tone='green'] {
            background: var(--site-green);
        }

        svg {
            width: 1.1rem;
            height: 1.1rem;
        }
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.9rem;
        color: var(--site-navy);
    }

    p {
        margin: 0.1rem 0 0;
        font-size: 0.8rem;
        color: var(--site-text-muted);
        line-height: 1.5;
    }
}

/* ---- why the site exists ---- */
.why {
    background: var(--site-gradient-navy);
    color: #fff;
}

.why-inner {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 940px) {
        grid-template-columns: 1fr;
    }

    h2 {
        color: #fff;
    }

    .body {
        margin: var(--site-space-md) 0 0;
        color: rgba(255, 255, 255, 0.86);
        line-height: 1.7;
        font-size: 0.95rem;
    }
}

.why-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.1rem;

    li {
        padding: 0.7rem 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.14);

        &:last-child {
            border-bottom: none;
        }
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.92rem;
        color: #fff;
        margin-bottom: 0.15rem;
    }

    p {
        margin: 0;
        font-size: 0.84rem;
        line-height: 1.55;
        color: rgba(255, 255, 255, 0.74);
    }
}

/* ---- behind ---- */
.behind-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: var(--site-space-md);
    margin-top: var(--site-space-lg);
}

.card {
    padding: var(--site-space-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.95rem;
        color: var(--site-navy);
        margin-bottom: 0.3rem;
    }

    p {
        margin: 0;
        font-size: 0.84rem;
        color: var(--site-text-muted);
        line-height: 1.6;
    }
}

/* ---- won't ---- */
.wont {
    background: var(--site-bg-alt);
}

.wont-inner {
    display: grid;
    grid-template-columns: 18rem minmax(0, 1fr);
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }

    ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: var(--site-space-md);
    }

    li {
        padding-left: 1.6rem;
        position: relative;

        &::before {
            content: '✕';
            position: absolute;
            left: 0;
            top: 0.05rem;
            font-weight: 700;
            color: var(--site-accent);
        }
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.92rem;
        color: var(--site-navy);
        margin-bottom: 0.15rem;
    }

    p {
        margin: 0;
        font-size: 0.85rem;
        color: var(--site-text-muted);
        line-height: 1.6;
    }
}

/* ---- closing ---- */
.closing-inner {
    text-align: center;
    max-width: 44rem;

    p {
        margin: 0 auto var(--site-space-lg);
        max-width: 34rem;
        color: var(--site-text-muted);
        line-height: 1.6;
    }
}

.actions {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: var(--site-space-sm);
}

.cta {
    padding: 0.75rem 1.4rem;
    border-radius: var(--site-radius-sm);
    background: var(--site-gradient-cta);
    color: #fff;
    font-weight: 700;
    font-size: 0.92rem;
    text-decoration: none;

    &:hover {
        background: var(--site-accent-strong);
    }

    &--ghost {
        background: none;
        border: 1px solid var(--site-border);
        color: var(--site-navy);

        &:hover {
            background: none;
            border-color: var(--site-blue);
            color: var(--site-blue);
        }
    }
}
</style>
